import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useAnnouncementQueue } from './useAnnouncementQueue';
import { useRecordAnnouncementEvent } from '@/hooks/useAnnouncements';
import { VIDEO_IFRAME_ALLOW, getVideoThumbnail, getYoutubeFallbackThumbnail } from '@/lib/videoEmbed';
import type { PlatformAnnouncement } from '@/types/platformContent';

const isExternal = (url: string) => /^https?:\/\//i.test(url);

interface Props {
  /** Comunicado forçado (teste no Super Admin). */
  previewAnnouncement?: PlatformAnnouncement | null;
  /** Não registra eventos nem consome a frequência. */
  disableTracking?: boolean;
  onClose?: () => void;
}

export const AnnouncementModalHost: React.FC<Props> = ({
  previewAnnouncement = null,
  disableTracking = false,
  onClose,
}) => {
  const { modal: queueModal } = useAnnouncementQueue();
  const { record: recordEvent } = useRecordAnnouncementEvent();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [neverShow, setNeverShow] = useState(false);
  const [videoThumb, setVideoThumb] = useState<string | null>(null);
  const shownRef = useRef<string | null>(null);

  const modal = previewAnnouncement || queueModal;

  const record = useCallback(
    (id: string, event: Parameters<typeof recordEvent>[1]) => {
      if (disableTracking) return;
      recordEvent(id, event);
    },
    [disableTracking, recordEvent],
  );

  useEffect(() => {
    if (modal && shownRef.current !== modal.id) {
      shownRef.current = modal.id;
      setNeverShow(false);
      setOpen(true);
      record(modal.id, 'shown');
    }
    if (!modal) shownRef.current = null;
  }, [modal, record]);

  // Capa do vídeo usada como fundo desfocado nas faixas vazias.
  useEffect(() => {
    let active = true;
    const url = modal?.video_url;
    if (!url) {
      setVideoThumb(null);
      return;
    }
    getVideoThumbnail(url).then((thumb) => {
      if (active) setVideoThumb(thumb);
    });
    return () => {
      active = false;
    };
  }, [modal?.video_url]);

  if (!modal) return null;
  if (!open) return null;

  const close = (kind: 'acknowledged' | 'dismissed' | 'postponed') => {
    if (neverShow) record(modal.id, 'never_show_again');
    record(modal.id, kind);
    setOpen(false);
    // Evita travamento de interações após fechar
    setTimeout(() => {
      document.body.style.pointerEvents = '';
      onClose?.();
    }, 300);
  };

  const handleCta = () => {
    record(modal.id, 'cta_clicked');
    const url = modal.cta_url;
    close('acknowledged');
    if (!url || disableTracking) return;
    setTimeout(() => {
      if (isExternal(url)) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        navigate(url);
      }
    }, 150);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && (modal.allow_dismiss || disableTracking)) close('dismissed');
      }}
    >
      <DialogContent
        className="dark bg-background text-foreground z-[350] max-h-[calc(100dvh-1rem)] max-w-[calc(100vw-1rem)] overflow-hidden p-0 gap-0 flex flex-col sm:max-h-[95dvh] sm:max-w-4xl lg:max-w-5xl [&>div]:min-h-0 [&>div]:p-0"
        onInteractOutside={(e) => {
          if (!modal.allow_dismiss && !disableTracking) e.preventDefault();
        }}
      >
        {/* A mídia reduz conforme a altura disponível, sem empurrar título e ações para fora. */}
        {modal.video_url ? (
          <div className="relative flex aspect-video w-full min-h-20 max-h-[58dvh] flex-[1_1_auto] items-center justify-center overflow-hidden bg-black">
            {videoThumb && (
              <img
                src={videoThumb}
                alt=""
                aria-hidden
                onError={(e) => {
                  const fallback = getYoutubeFallbackThumbnail(modal.video_url || '');
                  const img = e.currentTarget;
                  if (fallback && img.src !== fallback) img.src = fallback;
                  else setVideoThumb(null);
                }}
                className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover opacity-60 blur-2xl"
              />
            )}
            <iframe
              src={modal.video_url}
              title={modal.title}
              className="relative z-10 aspect-video h-full max-h-full w-auto max-w-full"
              allow={VIDEO_IFRAME_ALLOW}
              allowFullScreen
            />
          </div>
        ) : (
          modal.image_url && (
            <div className="relative flex w-full min-h-20 max-h-[42dvh] flex-[1_1_auto] items-center justify-center overflow-hidden bg-black">
              <img
                src={modal.image_url}
                alt=""
                aria-hidden
                className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover opacity-60 blur-2xl"
              />
              <img
                src={modal.image_url}
                alt={modal.title}
                className="relative z-10 h-full max-h-full w-auto max-w-full object-contain"
              />
            </div>
          )
        )}

        {/* O título permanece sempre visível; somente descrição e corpo podem rolar. */}
        <DialogHeader hideClose className="shrink-0 border-0 bg-transparent px-3 py-2 m-0 space-y-0 sm:px-4 sm:pt-3 sm:pb-1">
          <DialogTitle className="w-full text-base sm:text-lg leading-tight break-words whitespace-normal">
            {modal.title}
          </DialogTitle>
        </DialogHeader>

        {(modal.description || modal.body) && (
          <div className="min-h-0 flex-[0_1_auto] overflow-y-auto overscroll-contain px-3 pb-2 touch-pan-y sm:px-4 sm:pb-3">
            {modal.description && (
              <DialogDescription className="w-full text-xs leading-snug mt-0.5 break-words whitespace-normal">
                {modal.description}
              </DialogDescription>
            )}
            {modal.body && (
              <div
                className="prose prose-sm dark:prose-invert max-w-none mt-1 break-words"
                dangerouslySetInnerHTML={{ __html: modal.body }}
              />
            )}
          </div>
        )}

        {/* Rodapé fixo e flexível: os controles quebram linha em larguras reduzidas/zoom alto. */}
        <div className="shrink-0 px-3 py-2 sm:px-4 sm:pb-3 sm:pt-2 flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2">
          {modal.allow_never_show_again && (
            <label className="flex items-center gap-2 text-[11px] sm:text-xs text-muted-foreground cursor-pointer shrink-0">
              <Checkbox
                checked={neverShow}
                onCheckedChange={(v) => setNeverShow(Boolean(v))}
              />
              Não mostrar novamente
            </label>
          )}
          <div className="flex flex-wrap gap-2 sm:ml-auto w-full sm:w-auto">
            {(modal.allow_dismiss || disableTracking) && (
              <Button
                variant="outline"
                size="sm"
                className="min-w-24 flex-1 sm:flex-none"
                onClick={() => close('dismissed')}
              >
                Fechar
              </Button>
            )}
            {modal.cta_label && (
              <Button
                variant="secondary"
                size="sm"
                className="min-w-24 flex-1 sm:flex-none"
                onClick={handleCta}
              >
                {modal.cta_label}
              </Button>
            )}
            {modal.require_acknowledge ? (
              <Button size="sm" className="min-w-24 flex-1 sm:flex-none" onClick={() => close('acknowledged')}>
                Li e entendi
              </Button>
            ) : (
              <Button size="sm" className="min-w-24 flex-1 sm:flex-none" onClick={() => close('postponed')}>
                Ver depois
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AnnouncementModalHost;
